'use client';

import { SubmissionResult, useForm } from '@conform-to/react';
import * as Dialog from '@radix-ui/react-dialog';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import * as Popover from '@radix-ui/react-popover';
import { clsx } from 'clsx';
import debounce from 'lodash.debounce';
import {
  ArrowRight,
  ChevronDown,
  GiftIcon,
  Search,
  SearchIcon,
  ShoppingCart,
  User,
  X,
} from 'lucide-react';
import { useParams, useSearchParams } from 'next/navigation';
import React, {
  forwardRef,
  Ref,
  useActionState,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from 'react';
import { useFormStatus } from 'react-dom';

import { FormStatus } from '@/vibes/soul/form/form-status';
import { Stream, Streamable } from '@/vibes/soul/lib/streamable';
import { Button } from '@/vibes/soul/primitives/button';
import { Logo } from '@/vibes/soul/primitives/logo';
import { Price } from '@/vibes/soul/primitives/price-label';
import { ProductCard } from '@/vibes/soul/primitives/product-card';
import { Link } from '~/components/link';
import { TopNavFilter } from '~/components/vehicle-filter-form/components/top-nav-filter/top-nav-filter';
import { usePathname, useRouter } from '~/i18n/routing';
import { useSearch } from '~/lib/search';

interface Link {
  label: string;
  href: string;
  groups?: Array<{
    label?: string;
    href?: string;
    links: Array<{
      label: string;
      href: string;
    }>;
  }>;
}

interface Locale {
  id: string;
  label: string;
}

interface Currency {
  id: string;
  label: string;
}

type Action<State, Payload> = (
  state: Awaited<State>,
  payload: Awaited<Payload>,
) => State | Promise<State>;

export type SearchResult =
  | {
    type: 'products';
    title: string;
    products: Array<{
      id: string;
      title: string;
      href: string;
      price?: Price;
      image?: { src: string; alt: string };
    }>;
  }
  | {
    type: 'links';
    title: string;
    links: Array<{ label: string; href: string }>;
  };

type CurrencyAction = Action<SubmissionResult | null, FormData>;
type SearchAction<S extends SearchResult> = Action<
  {
    searchResults: S[] | null;
    lastResult: SubmissionResult | null;
    emptyStateTitle?: string;
    emptyStateSubtitle?: string;
  },
  FormData
>;

interface Props<S extends SearchResult> {
  className?: string;
  isFloating?: boolean;
  accountHref: string;
  cartCount?: Streamable<number | null>;
  cartHref: string;
  links: Streamable<Link[]>;
  linksPosition?: 'center' | 'left' | 'right';
  locales?: Locale[];
  activeLocaleId?: string;
  currencies?: Currency[];
  activeCurrencyId?: Streamable<string | undefined>;
  currencyAction?: CurrencyAction;
  logo?: Streamable<string | { src: string; alt: string } | null>;
  logoWidth?: number;
  logoHeight?: number;
  logoHref?: string;
  logoLabel?: string;
  mobileLogo?: Streamable<string | { src: string; alt: string } | null>;
  mobileLogoWidth?: number;
  mobileLogoHeight?: number;
  searchHref: string;
  searchParamName?: string;
  searchAction?: SearchAction<S>;
  searchInputPlaceholder?: string;
  searchSubmitLabel?: string;
  cartLabel?: string;
  accountLabel?: string;
  openSearchPopupLabel?: string;
  searchLabel?: string;
  mobileMenuTriggerLabel?: string;
  mobileMenuCloseLabel?: string;
  mobileMenuShopAllLabel?: string;
  switchCurrencyLabel?: string;
  giftCertificatesLabel?: string;
  giftCertificatesHref: string;
  giftCertificatesEnabled?: Streamable<boolean>;
}

const MobileMenuButton = forwardRef<
  React.ComponentRef<'button'>,
  { open: boolean } & React.ComponentPropsWithoutRef<'button'>
>(({ open, className, ...rest }, ref) => {
  return (
    <button
      {...rest}
      className={clsx(
        'group relative p-2 outline-0 ring-[var(--nav-focus,hsl(var(--primary)))] transition-colors focus-visible:ring-2 pl-6',
        className,
      )}
      ref={ref}
    >
      <div className="flex h-4 w-4 origin-center transform flex-col justify-between overflow-hidden transition-all duration-300">
        <div
          className={clsx(
            'h-px origin-left transform bg-[var(--nav-mobile-button-icon,hsl(var(--foreground)))] transition-all duration-300',
            open ? 'translate-x-10' : 'w-7',
          )}
        />
        <div
          className={clsx(
            'h-px transform rounded bg-[var(--nav-mobile-button-icon,hsl(var(--foreground)))] transition-all delay-75 duration-300',
            open ? 'translate-x-10' : 'w-7',
          )}
        />
        <div
          className={clsx(
            'h-px origin-left transform bg-[var(--nav-mobile-button-icon,hsl(var(--foreground)))] transition-all delay-150 duration-300',
            open ? 'translate-x-10' : 'w-7',
          )}
        />

        <div
          className={clsx(
            'absolute top-2 flex transform items-center justify-between bg-[var(--nav-mobile-button-icon,hsl(var(--foreground)))] transition-all duration-500',
            open ? 'w-12 translate-x-0' : 'w-0 -translate-x-10',
          )}
        >
          <div
            className={clsx(
              'absolute h-px w-4 transform bg-[var(--nav-mobile-button-icon,hsl(var(--foreground)))] transition-all delay-300 duration-500',
              open ? 'rotate-45' : 'rotate-0',
            )}
          />
          <div
            className={clsx(
              'absolute h-px w-4 transform bg-[var(--nav-mobile-button-icon,hsl(var(--foreground)))] transition-all delay-300 duration-500',
              open ? '-rotate-45' : 'rotate-0',
            )}
          />
        </div>
      </div>
    </button>
  );
});

MobileMenuButton.displayName = 'MobileMenuButton';

const navGroupClassName =
  'block bg-[var(--nav-group-background,transparent)] px-3 py-4 font-[family-name:var(--nav-group-font-family,var(--font-family-body))] font-medium text-[var(--nav-group-text,hsl(var(--foreground)))] ring-[var(--nav-focus,hsl(var(--primary)))] transition-colors hover:bg-[var(--nav-group-background-hover,hsl(var(--contrast-100)))] hover:text-[var(--nav-group-text-hover,hsl(var(--foreground)))] focus-visible:outline-0 focus-visible:ring-2';
const navButtonClassName =
  'relative bg-[var(--nav-button-background,transparent)] p-1.5 text-[var(--nav-button-icon,hsl(var(--foreground)))] ring-[var(--nav-focus,hsl(var(--primary)))] transition-colors focus-visible:outline-0 focus-visible:ring-2 @4xl:hover:bg-[var(--nav-button-background-hover,hsl(var(--contrast-100)))] @4xl:hover:text-[var(--nav-button-icon-hover,hsl(var(--foreground)))]';
const mobileNavLinkClassName =
  'block bg-[var(--nav-mobile-link-background,transparent)] px-3 py-2 font-[family-name:var(--nav-mobile-link-font-family,var(--font-family-body))] font-semibold text-[var(--nav-mobile-link-text,hsl(var(--foreground)))] ring-[var(--nav-focus,hsl(var(--primary)))] transition-colors hover:bg-[var(--nav-mobile-link-background-hover,hsl(var(--contrast-100)))] hover:text-[var(--nav-mobile-link-text-hover,hsl(var(--foreground)))] focus-visible:outline-0 focus-visible:ring-2';
const mobileNavGroupClassName =
  'block bg-[var(--nav-group-background,transparent)] px-6 py-2 font-[family-name:var(--nav-group-font-family,var(--font-family-body))] text-sm font-medium text-[var(--nav-group-text,hsl(var(--foreground)))] ring-[var(--nav-focus,hsl(var(--primary)))] transition-colors hover:bg-[var(--nav-group-background-hover,hsl(var(--contrast-100)))] hover:text-[var(--nav-group-text-hover,hsl(var(--foreground)))] focus-visible:outline-0 focus-visible:ring-2';
const mobileNavSubLinkClassName =
  'block bg-[var(--nav-mobile-sub-link-background,transparent)] px-9 py-2 font-[family-name:var(--nav-mobile-sub-link-font-family,var(--font-family-body))] text-sm font-medium text-[var(--nav-mobile-sub-link-text,hsl(var(--contrast-500)))] ring-[var(--nav-focus,hsl(var(--primary)))] transition-colors hover:bg-[var(--nav-mobile-sub-link-background-hover,hsl(var(--contrast-100)))] hover:text-[var(--nav-mobile-sub-link-text-hover,hsl(var(--foreground)))] focus-visible:outline-0 focus-visible:ring-2';
const mobileNavShopAllClassName =
  'block bg-[var(--nav-mobile-sub-link-background,transparent)] px-6 py-2 font-[family-name:var(--nav-mobile-sub-link-font-family,var(--font-family-body))] text-sm font-semibold text-[var(--nav-mobile-link-text,hsl(var(--foreground)))] ring-[var(--nav-focus,hsl(var(--primary)))] transition-colors hover:bg-[var(--nav-mobile-sub-link-background-hover,hsl(var(--contrast-100)))] hover:text-[var(--nav-mobile-link-text-hover,hsl(var(--foreground)))] focus-visible:outline-0 focus-visible:ring-2';
const mobileNavAccordionTriggerClassName =
  'group flex w-full items-center justify-between gap-2 bg-[var(--nav-mobile-link-background,transparent)] px-3 py-2 font-[family-name:var(--nav-mobile-link-font-family,var(--font-family-body))] font-semibold text-[var(--nav-mobile-link-text,hsl(var(--foreground)))] ring-[var(--nav-focus,hsl(var(--primary)))] transition-colors hover:bg-[var(--nav-mobile-link-background-hover,hsl(var(--contrast-100)))] hover:text-[var(--nav-mobile-link-text-hover,hsl(var(--foreground)))] focus-visible:outline-0 focus-visible:ring-2';
const mobileNavGroupAccordionTriggerClassName =
  'group flex w-full items-center justify-between gap-2 bg-[var(--nav-group-background,transparent)] px-6 py-2 font-[family-name:var(--nav-group-font-family,var(--font-family-body))] text-sm font-medium text-[var(--nav-group-text,hsl(var(--foreground)))] ring-[var(--nav-focus,hsl(var(--primary)))] transition-colors hover:bg-[var(--nav-group-background-hover,hsl(var(--contrast-100)))] hover:text-[var(--nav-group-text-hover,hsl(var(--foreground)))] focus-visible:outline-0 focus-visible:ring-2';
const mobileNavLinkActiveClassName =
  'bg-[var(--nav-mobile-link-background-hover,hsl(var(--contrast-100)))] text-[var(--nav-mobile-link-text-hover,hsl(var(--foreground)))]';
const mobileNavGroupActiveClassName =
  'bg-[var(--nav-group-background-hover,hsl(var(--contrast-100)))] text-[var(--nav-group-text-hover,hsl(var(--foreground)))] font-semibold';
const mobileNavSubLinkActiveClassName =
  'bg-[var(--nav-mobile-sub-link-background-hover,hsl(var(--contrast-100)))] font-semibold text-[var(--nav-mobile-sub-link-text-hover,hsl(var(--foreground)))]';

function normalizeNavPath(path?: string | null): string {
  if (path == null || path === '' || path === '#') {
    return '';
  }

  if (path.length > 1 && path.endsWith('/')) {
    return path.slice(0, -1);
  }

  return path;
}

function isMobileNavPathActive(pathname: string, href?: string | null): boolean {
  const normalizedHref = normalizeNavPath(href);

  if (normalizedHref === '') {
    return false;
  }

  const normalizedPathname = normalizeNavPath(pathname);

  return (
    normalizedPathname === normalizedHref ||
    normalizedPathname.startsWith(`${normalizedHref}/`)
  );
}

function isMobileNavPathExactActive(pathname: string, href?: string | null): boolean {
  const normalizedHref = normalizeNavPath(href);

  if (normalizedHref === '') {
    return false;
  }

  return normalizeNavPath(pathname) === normalizedHref;
}

function mobileNavGroupHasActiveChild(
  pathname: string,
  group: NonNullable<Link['groups']>[number],
): boolean {
  if (isMobileNavPathActive(pathname, group.href)) {
    return true;
  }

  return group.links.some((link) => isMobileNavPathActive(pathname, link.href));
}

function mobileNavItemHasActiveChild(pathname: string, item: Link): boolean {
  if (isMobileNavPathActive(pathname, item.href)) {
    return true;
  }

  return item.groups?.some((group) => mobileNavGroupHasActiveChild(pathname, group)) ?? false;
}

/**
 * This component supports various CSS variables for theming. Here's a comprehensive list, along
 * with their default values:
 *
 * ```css
 * :root {
 *   --nav-focus: hsl(var(--primary));
 *   --nav-background: hsl(var(--background));
 *   --nav-floating-border: hsl(var(--foreground) / 10%);
 *   --nav-link-text: hsl(var(--foreground));
 *   --nav-link-text-hover: hsl(var(--foreground));
 *   --nav-link-background: transparent;
 *   --nav-link-background-hover: hsl(var(--contrast-100));
 *   --nav-link-font-family: var(--font-family-body);
 *   --nav-group-text: hsl(var(--foreground));
 *   --nav-group-text-hover: hsl(var(--foreground));
 *   --nav-group-background: transparent;
 *   --nav-group-background-hover: hsl(var(--contrast-100));
 *   --nav-group-font-family: var(--font-family-body);
 *   --nav-sub-link-text: hsl(var(--contrast-500));
 *   --nav-sub-link-text-hover: hsl(var(--foreground));
 *   --nav-sub-link-background: transparent;
 *   --nav-sub-link-background-hover: hsl(var(--contrast-100));
 *   --nav-sub-link-font-family: var(--font-family-body);
 *   --nav-button-icon: hsl(var(--foreground));
 *   --nav-button-icon-hover: hsl(var(--foreground));
 *   --nav-button-background: hsl(var(--background));
 *   --nav-button-background-hover: hsl(var(--contrast-100));
 *   --nav-menu-background: hsl(var(--background));
 *   --nav-menu-border: hsl(var(--foreground) / 5%);
 *   --nav-mobile-background: hsl(var(--background));
 *   --nav-mobile-divider: hsl(var(--contrast-100));
 *   --nav-mobile-button-icon: hsl(var(--foreground));
 *   --nav-mobile-link-text: hsl(var(--foreground));
 *   --nav-mobile-link-text-hover: hsl(var(--foreground));
 *   --nav-mobile-link-background: transparent;
 *   --nav-mobile-link-background-hover: hsl(var(--contrast-100));
 *   --nav-mobile-link-font-family: var(--font-family-body);
 *   --nav-mobile-sub-link-text: hsl(var(--contrast-500));
 *   --nav-mobile-sub-link-text-hover: hsl(var(--foreground));
 *   --nav-mobile-sub-link-background: transparent;
 *   --nav-mobile-sub-link-background-hover: hsl(var(--contrast-100));
 *   --nav-mobile-sub-link-font-family: var(--font-family-body);
 *   --nav-search-background: hsl(var(--background));
 *   --nav-search-border: hsl(var(--foreground) / 5%);
 *   --nav-search-divider: hsl(var(--foreground) / 5%);
 *   --nav-search-icon: hsl(var(--contrast-500));
 *   --nav-search-empty-title: hsl(var(--foreground));
 *   --nav-search-empty-subtitle: hsl(var(--contrast-500));
 *   --nav-search-result-title: hsl(var(--foreground));
 *   --nav-search-result-title-font-family: var(--font-family-mono);
 *   --nav-search-result-link-text: hsl(var(--foreground));
 *   --nav-search-result-link-text-hover: hsl(var(--foreground));
 *   --nav-search-result-link-background: hsl(var(--background));
 *   --nav-search-result-link-background-hover: hsl(var(--contrast-100));
 *   --nav-search-result-link-font-family: var(--font-family-body);
 *   --nav-cart-count-text: hsl(var(--background));
 *   --nav-cart-count-background: hsl(var(--foreground));
 *   --nav-locale-background: hsl(var(--background));
 *   --nav-locale-link-text: hsl(var(--contrast-400));
 *   --nav-locale-link-text-hover: hsl(var(--foreground));
 *   --nav-locale-link-text-selected: hsl(var(--foreground));
 *   --nav-locale-link-background: transparent;
 *   --nav-locale-link-background-hover: hsl(var(--contrast-100));
 *   --nav-locale-link-font-family: var(--font-family-body);
 * }
 * ```
 */
export const Navigation = forwardRef(function Navigation<S extends SearchResult>(
  {
    className,
    isFloating = false,
    cartHref,
    cartCount: streamableCartCount,
    accountHref,
    links: streamableLinks,
    logo: streamableLogo,
    logoHref = '/',
    logoLabel = 'Home',
    logoWidth = 200,
    logoHeight = 40,
    mobileLogo: streamableMobileLogo,
    mobileLogoWidth = 100,
    mobileLogoHeight = 40,
    linksPosition = 'center',
    activeLocaleId,
    locales,
    currencies: streamableCurrencies,
    activeCurrencyId: streamableActiveCurrencyId,
    currencyAction,
    searchHref,
    searchParamName = 'query',
    searchAction,
    searchInputPlaceholder,
    searchSubmitLabel,
    cartLabel = 'Cart',
    accountLabel = 'Profile',
    openSearchPopupLabel = 'Open search popup',
    searchLabel = 'Search',
    mobileMenuTriggerLabel = 'Toggle navigation',
    mobileMenuCloseLabel = 'Close menu',
    mobileMenuShopAllLabel = 'Shop All',
    switchCurrencyLabel,
    giftCertificatesLabel = 'Gift Certificates',
    giftCertificatesHref,
    giftCertificatesEnabled: streamableGiftCertificatesEnabled,
  }: Props<S>,
  ref: Ref<HTMLDivElement>,
) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isSearchOpen, setIsSearchOpen } = useSearch();

  const pathname = usePathname();

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsSearchOpen(false);
  }, [pathname, setIsSearchOpen]);

  useEffect(() => {
    function handleScroll() {
      setIsSearchOpen(false);
      setIsMobileMenuOpen(false);
    }

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [setIsSearchOpen]);

  return (
    <NavigationMenu.Root
      className={clsx('relative w-full @container bg-[var(--nav-background,hsl(var(--background-primary-default)))] border-b ', className)}
      delayDuration={0}
      onValueChange={() => setIsSearchOpen(false)}
      ref={ref}
    >
      <div className='relative z-[60] bg-[var(--header-background,hsl(var(--background-primary-black)))]'>
        <div className={clsx(
          'mx-auto max-w-screen-2xl flex items-center justify-between gap-1 transition-shadow @4xl:p-5',
          isFloating
            ? 'shadow-xl ring-1 ring-[var(--nav-floating-border,hsl(var(--foreground)/10%))]'
            : 'shadow-none ring-0',
        )}
        >
          {/* Mobile Menu */}
          <Dialog.Root onOpenChange={setIsMobileMenuOpen} open={isMobileMenuOpen}>
            <Dialog.Trigger asChild>
              <MobileMenuButton
                aria-label={mobileMenuTriggerLabel}
                className="mr-1 @4xl:hidden basis-1/6 @lg:basis-1/3"
                open={isMobileMenuOpen}
              />
            </Dialog.Trigger>
            <Dialog.Portal>
              <Dialog.Content
                className="fixed inset-y-0 left-0 z-[70] flex h-[100dvh] max-h-[100dvh] w-full flex-col overflow-hidden bg-white shadow-none focus:outline-none data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 max-sm:data-[state=closed]:slide-out-to-top max-sm:data-[state=open]:slide-in-from-top sm:w-[60%] sm:max-w-[60%] sm:shadow-xl sm:data-[state=closed]:slide-out-to-left sm:data-[state=open]:slide-in-from-left @4xl:hidden"
                onOpenAutoFocus={(event) => event.preventDefault()}
              >
                <Dialog.Title className="sr-only">{mobileMenuTriggerLabel}</Dialog.Title>
                <div className="flex shrink-0 items-center justify-end px-4 py-3">
                  <Dialog.Close asChild>
                    <button
                      aria-label={mobileMenuCloseLabel}
                      className={navButtonClassName}
                      type="button"
                    >
                      <X size={24} strokeWidth={2} />
                    </button>
                  </Dialog.Close>
                </div>
                <div className="min-h-0 flex-1 touch-pan-y overflow-y-scroll overscroll-y-contain [-webkit-overflow-scrolling:touch]">
                  <div className="divide-y divide-[var(--nav-mobile-divider,hsl(var(--contrast-100)))] bg-white pb-8">
                    <Stream
                      fallback={
                        <ul className="flex animate-pulse flex-col gap-4 p-5 @4xl:gap-2 @4xl:p-5">
                          <li>
                            <span className="block h-4 w-10 rounded-md bg-contrast-100" />
                          </li>
                          <li>
                            <span className="block h-4 w-14 rounded-md bg-contrast-100" />
                          </li>
                          <li>
                            <span className="block h-4 w-24 rounded-md bg-contrast-100" />
                          </li>
                          <li>
                            <span className="block h-4 w-16 rounded-md bg-contrast-100" />
                          </li>
                        </ul>
                      }
                      value={streamableLinks}
                    >
                      {(links) => (
                        <MobileNavLinks
                          key={isMobileMenuOpen ? `open-${pathname}` : 'closed'}
                          links={links}
                          pathname={pathname}
                          shopAllLabel={mobileMenuShopAllLabel}
                        />
                      )}
                    </Stream>
                    {/* Mobile Locale / Currency Dropdown */}
                    {locales && locales.length > 1 && streamableCurrencies && (
                      <div className="p-2 @4xl:p-5">
                        <div className="flex items-center px-3 py-1 @4xl:py-2">
                          {/* Locale / Language Dropdown */}
                          {locales.length > 1 ? (
                            <LocaleSwitcher
                              activeLocaleId={activeLocaleId}
                              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                              locales={locales as [Locale, Locale, ...Locale[]]}
                            />
                          ) : null}

                          {/* Currency Dropdown */}
                          <Stream
                            fallback={null}
                            value={Streamable.all([streamableCurrencies, streamableActiveCurrencyId])}
                          >
                            {([currencies, activeCurrencyId]) =>
                              currencies.length > 1 && currencyAction ? (
                                <CurrencyForm
                                  action={currencyAction}
                                  activeCurrencyId={activeCurrencyId}
                                  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                                  currencies={currencies as [Currency, ...Currency[]]}
                                />
                              ) : null
                            }
                          </Stream>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>

          {/* Logo */}
          <Logo
            className={clsx(streamableMobileLogo != null ? 'hidden @4xl:flex lg:w-full' : 'flex lg:w-full')}
            height={logoHeight}
            href={logoHref}
            label={logoLabel}
            logo={streamableLogo}
            width={logoWidth}
          />
          {streamableMobileLogo != null && (
            <Logo
              className="flex @4xl:hidden"
              height={mobileLogoHeight}
              href={logoHref}
              label={logoLabel}
              logo={streamableMobileLogo}
              width={mobileLogoWidth}
            />
          )}
          {/* Icon Buttons */}
          <div
            className={clsx(
              'flex items-center justify-end gap-1 lg:gap-6 transition-colors duration-300',
              linksPosition === 'center' ? 'flex-1' : 'flex-1 @4xl:flex-none pr-4 @3xl:pr-0',
            )}
          >
            {searchAction ? (
              <Popover.Root onOpenChange={setIsSearchOpen} open={isSearchOpen}>
                <Popover.Anchor
                  className="absolute left-0 right-0 top-full z-10"
                />
                <Popover.Trigger asChild>
                  <button
                    aria-label={openSearchPopupLabel}
                    className={navButtonClassName}
                    onPointerEnter={(e) => e.preventDefault()}
                    onPointerLeave={(e) => e.preventDefault()}
                    onPointerMove={(e) => e.preventDefault()}
                  >
                    <Search size={24} strokeWidth={2} />
                  </button>
                </Popover.Trigger>
                <Popover.Portal>
                  <Popover.Content
                    className="max-h-[calc(var(--radix-popover-content-available-height)-16px)] w-[var(--radix-popper-anchor-width)] @container data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 z-50"
                    side="bottom"
                    align="start"
                    sideOffset={0}
                    avoidCollisions={false}
                  >
                    <div className="flex max-h-[inherit] flex-col bg-[var(--nav-search-background,hsl(var(--background)))] shadow-xl ring-1 ring-[var(--nav-search-border,hsl(var(--foreground)/5%))] transition-all duration-200 ease-in-out @4xl:inset-x-0">
                      <SearchForm
                        searchAction={searchAction}
                        searchHref={searchHref}
                        searchInputPlaceholder={searchInputPlaceholder}
                        searchParamName={searchParamName}
                        searchSubmitLabel={searchSubmitLabel}
                      />
                    </div>
                  </Popover.Content>
                </Popover.Portal>
              </Popover.Root>
            ) : (
              <Link aria-label={searchLabel} className={navButtonClassName} href={searchHref}>
                <Search size={24} strokeWidth={2} />
              </Link>
            )}

            <Link aria-label={accountLabel} className={navButtonClassName} href={accountHref}>
              <User size={24} strokeWidth={2} />
            </Link>
            <Link aria-label={cartLabel} className={navButtonClassName} href={cartHref}>
              <ShoppingCart size={24} strokeWidth={2} />
              <Stream
                fallback={
                  <span className="absolute -right-1 -top-1 flex h-4 w-4 animate-pulse items-center justify-center rounded-full bg-contrast-100 text-xs text-background" />
                }
                value={streamableCartCount}
              >
                {(cartCount) =>
                  cartCount != null &&
                  cartCount > 0 && (
                    <span className="absolute -right-1 -top-1 flex h-4 w-4 p-2 items-center border-2 border-white justify-center rounded-full bg-[var(--nav-cart-count-background,hsl(var(--foreground)))] font-[family-name:var(--nav-cart-count-font-family,var(--font-family-body))] text-xs text-[var(--nav-cart-count-text,hsl(var(--background)))]">
                      {cartCount}
                    </span>
                  )
                }
              </Stream>
            </Link>

            <Stream fallback={null} value={streamableGiftCertificatesEnabled}>
              {(giftCertificatesEnabled) =>
                giftCertificatesEnabled && (
                  <Link
                    aria-label={giftCertificatesLabel}
                    className={navButtonClassName}
                    href={giftCertificatesHref}
                  >
                    <GiftIcon size={20} strokeWidth={1} />
                  </Link>
                )
              }
            </Stream>

            {/* Locale / Language Dropdown */}
            {locales && locales.length > 1 ? (
              <LocaleSwitcher
                activeLocaleId={activeLocaleId}
                className="hidden @4xl:block"
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                locales={locales as [Locale, Locale, ...Locale[]]}
              />
            ) : null}

            {/* Currency Dropdown */}
            <Stream
              fallback={null}
              value={Streamable.all([streamableCurrencies, streamableActiveCurrencyId])}
            >
              {([currencies, activeCurrencyId]) =>
                currencies && currencies.length > 1 && currencyAction ? (
                  <CurrencyForm
                    action={currencyAction}
                    activeCurrencyId={activeCurrencyId}
                    className="hidden @4xl:block"
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    currencies={currencies as [Currency, ...Currency[]]}
                    switchCurrencyLabel={switchCurrencyLabel}
                  />
                ) : null
              }
            </Stream>
          </div>
        </div>
      </div>
      {/* Top Level Nav Links */}
      <ul
        className={clsx(
          'hidden gap-1 @4xl:flex @4xl:flex-1 mx-auto max-w-screen-2xl items-center px-5 lg:justify-between',
          {
            left: '@4xl:justify-start',
            center: '@4xl:justify-center',
            right: '@4xl:justify-end',
          }[linksPosition],
        )}
      >
        <TopNavFilter />
        <Stream
          fallback={
            <ul className="flex min-h-[41px] animate-pulse flex-row items-center @4xl:gap-6 @4xl:p-2.5">
              <li>
                <span className="block h-4 w-10 rounded-md bg-contrast-100" />
              </li>
              <li>
                <span className="block h-4 w-14 rounded-md bg-contrast-100" />
              </li>
              <li>
                <span className="block h-4 w-24 rounded-md bg-contrast-100" />
              </li>
              <li>
                <span className="block h-4 w-16 rounded-md bg-contrast-100" />
              </li>
            </ul>
          }
          value={streamableLinks}
        >
          {(links) =>
            links.map((item, i) => (
              <NavigationMenu.Item key={i} value={i.toString()}>
                <NavigationMenu.Trigger asChild>
                  <Link
                    className="hidden items-center whitespace-nowrap font-bold uppercase bg-[var(--nav-link-background,transparent)] p-5 @3xl:px-3 @5xl:px-5 font-[family-name:var(--nav-link-font-family,var(--font-family-body))] text-[var(--nav-link-text,hsl(var(--foreground)))] ring-[var(--nav-focus,hsl(var(--primary)))] transition-colors duration-200 hover:bg-[var(--nav-link-background-hover,hsl(var(--contrast-100)))] hover:text-[var(--nav-link-text-hover,hsl(var(--foreground)))] focus-visible:outline-0 focus-visible:ring-2 @4xl:inline-flex"
                    href={item.href}
                  >
                    {item.label}
                  </Link>
                </NavigationMenu.Trigger>
                {item.groups != null && item.groups.length > 0 && (
                  <NavigationMenu.Content className="bg-[var(--nav-menu-background,hsl(var(--background)))] shadow-xl ring-1 ring-[var(--nav-menu-border,hsl(var(--foreground)/5%))]">
                    <div className="m-auto w-full max-w-screen-2xl columns-4 justify-center gap-5 px-5 pb-8 pt-5">
                      {item.groups.map((group, columnIndex) => (
                        <ul className="flex flex-col" key={columnIndex}>
                          {/* Second Level Links */}
                          {group.label != null && group.label !== '' && (
                            <li>
                              {group.href != null && group.href !== '' ? (
                                <Link className={navGroupClassName} href={group.href}>
                                  {group.label}
                                </Link>
                              ) : (
                                <span className={navGroupClassName}>{group.label}</span>
                              )}
                            </li>
                          )}
                        </ul>
                      ))}
                    </div>
                  </NavigationMenu.Content>
                )}
              </NavigationMenu.Item>
            ))
          }
        </Stream>
      </ul>
      <div className="perspective-[2000px] absolute left-0 right-0 top-full z-50 flex w-full justify-center">
        <NavigationMenu.Viewport className="relative w-full data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95" />
      </div>
    </NavigationMenu.Root>
  );
});

Navigation.displayName = 'Navigation';

function MobileNavAccordionChevron({ className }: { className?: string }) {
  return (
    <ChevronDown
      className={clsx(
        'size-4 shrink-0 text-[var(--nav-mobile-link-text,hsl(var(--foreground)))] transition-transform duration-300',
        className,
      )}
      strokeWidth={2}
    />
  );
}

function MobileNavExpandable({
  label,
  triggerClassName,
  activeClassName = mobileNavLinkActiveClassName,
  className,
  children,
  defaultOpen = false,
  isActive = false,
}: {
  label: string;
  triggerClassName: string;
  activeClassName?: string;
  className?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  isActive?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => {
    setOpen(defaultOpen);
  }, [defaultOpen]);

  return (
    <div
      className={clsx(
        'border-b border-[var(--nav-mobile-divider,hsl(var(--contrast-100)))] last:border-b-0',
        className,
      )}
    >
      <button
        aria-expanded={open}
        className={clsx(triggerClassName, isActive && activeClassName)}
        onClick={() => setOpen((current) => !current)}
        type="button"
      >
        <span>{label}</span>
        <MobileNavAccordionChevron className={open ? 'rotate-180' : undefined} />
      </button>
      {open ? <div>{children}</div> : null}
    </div>
  );
}

function MobileNavLinks({
  links,
  pathname,
  shopAllLabel,
}: {
  links: Link[];
  pathname: string;
  shopAllLabel: string;
}) {
  return (
    <div className="flex flex-col p-2">
      {links.map((item, itemIndex) => {
        const hasGroups = item.groups != null && item.groups.length > 0;
        const isItemActive = mobileNavItemHasActiveChild(pathname, item);

        if (!hasGroups) {
          if (item.label === '') return null;

          const isActive = isMobileNavPathActive(pathname, item.href);

          return (
            <Link
              aria-current={isActive ? 'page' : undefined}
              className={clsx(mobileNavLinkClassName, isActive && mobileNavLinkActiveClassName)}
              href={item.href}
              key={itemIndex}
            >
              {item.label}
            </Link>
          );
        }

        return (
          <MobileNavExpandable
            defaultOpen={isItemActive}
            isActive={isItemActive}
            key={itemIndex}
            label={item.label}
            triggerClassName={mobileNavAccordionTriggerClassName}
          >
            <div className="pb-2">
              {item.href !== '' && (
                <Link
                  aria-current={
                    isMobileNavPathExactActive(pathname, item.href) ? 'page' : undefined
                  }
                  className={clsx(
                    mobileNavShopAllClassName,
                    isMobileNavPathActive(pathname, item.href) && mobileNavLinkActiveClassName,
                  )}
                  href={item.href}
                >
                  {shopAllLabel}
                </Link>
              )}
              {item.groups?.map((group, groupIndex) => {
                const hasSubLinks = group.links.length > 0;
                const groupLabel = group.label ?? '';
                const isGroupActive = mobileNavGroupHasActiveChild(pathname, group);

                if (!hasSubLinks) {
                  if (groupLabel === '') return null;

                  return group.href != null && group.href !== '' ? (
                    <Link
                      aria-current={isGroupActive ? 'page' : undefined}
                      className={clsx(
                        mobileNavGroupClassName,
                        isGroupActive && mobileNavGroupActiveClassName,
                      )}
                      href={group.href}
                      key={groupIndex}
                    >
                      {groupLabel}
                    </Link>
                  ) : (
                    <span
                      className={clsx(
                        mobileNavGroupClassName,
                        isGroupActive && mobileNavGroupActiveClassName,
                      )}
                      key={groupIndex}
                    >
                      {groupLabel}
                    </span>
                  );
                }

                const groupTriggerLabel = groupLabel !== '' ? groupLabel : shopAllLabel;

                return (
                  <MobileNavExpandable
                    activeClassName={mobileNavGroupActiveClassName}
                    className="border-t border-[var(--nav-mobile-divider,hsl(var(--contrast-100)))]"
                    defaultOpen={isGroupActive}
                    isActive={isGroupActive}
                    key={groupIndex}
                    label={groupTriggerLabel}
                    triggerClassName={mobileNavGroupAccordionTriggerClassName}
                  >
                    <div className="pb-2">
                      {group.href != null && group.href !== '' && (
                        <Link
                          aria-current={
                            isMobileNavPathExactActive(pathname, group.href) ? 'page' : undefined
                          }
                          className={clsx(
                            mobileNavShopAllClassName,
                            isMobileNavPathActive(pathname, group.href) &&
                            mobileNavLinkActiveClassName,
                          )}
                          href={group.href}
                        >
                          {shopAllLabel}
                        </Link>
                      )}
                      <ul className="flex flex-col">
                        {group.links.map((link, linkIndex) => {
                          const isActive = isMobileNavPathExactActive(pathname, link.href);

                          return (
                            <li key={linkIndex}>
                              <Link
                                aria-current={isActive ? 'page' : undefined}
                                className={clsx(
                                  mobileNavSubLinkClassName,
                                  isActive && mobileNavSubLinkActiveClassName,
                                )}
                                href={link.href || '#'}
                              >
                                {link.label}
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </MobileNavExpandable>
                );
              })}
            </div>
          </MobileNavExpandable>
        );
      })}
    </div>
  );
}

function SearchForm<S extends SearchResult>({
  searchAction,
  searchParamName = 'query',
  searchHref = '/search',
  searchInputPlaceholder = 'Search Products',
  searchSubmitLabel = 'Submit',
}: {
  searchAction: SearchAction<S>;
  searchParamName?: string;
  searchHref?: string;
  searchInputPlaceholder?: string;
  searchSubmitLabel?: string;
}) {
  const [query, setQuery] = useState('');
  const [isSearching, startSearching] = useTransition();
  const [{ searchResults, lastResult, emptyStateTitle, emptyStateSubtitle }, formAction] =
    useActionState(searchAction, {
      searchResults: null,
      lastResult: null,
    });
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isPending = isSearching || isDebouncing || isSubmitting;
  const debouncedOnChange = useMemo(() => {
    const debounced = debounce((q: string) => {
      setIsDebouncing(false);

      const formData = new FormData();

      formData.append(searchParamName, q);

      startSearching(() => {
        formAction(formData);
      });
    }, 300);

    return (q: string) => {
      setIsDebouncing(true);

      debounced(q);
    };
  }, [formAction, searchParamName]);

  const [form] = useForm({ lastResult });

  const handleSubmit = useCallback(() => {
    setIsSubmitting(true);
  }, []);

  return (
    <>
      <form
        action={searchHref}
        className="flex items-center gap-3 px-3 py-3 @4xl:px-5 @4xl:py-4"
        onSubmit={handleSubmit}
      >
        <SearchIcon
          className="hidden shrink-0 text-[var(--nav-search-icon,hsl(var(--contrast-500)))] @xl:block"
          size={20}
          strokeWidth={1}
        />
        <input
          className="grow bg-transparent pl-2 text-lg font-medium outline-0 focus-visible:outline-none @xl:pl-0"
          name={searchParamName}
          onChange={(e) => {
            setQuery(e.currentTarget.value);
            debouncedOnChange(e.currentTarget.value);
          }}
          placeholder={searchInputPlaceholder}
          type="text"
          value={query}
        />
        <SubmitButton loading={isPending} submitLabel={searchSubmitLabel} />
      </form>

      <SearchResults
        emptySearchSubtitle={emptyStateSubtitle}
        emptySearchTitle={emptyStateTitle}
        errors={form.errors}
        query={query}
        searchParamName={searchParamName}
        searchResults={searchResults}
        stale={isPending}
      />
    </>
  );
}

function SubmitButton({ loading, submitLabel }: { loading: boolean; submitLabel: string }) {
  const { pending } = useFormStatus();

  return (
    <Button
      loading={pending || loading}
      shape="circle"
      size="small"
      type="submit"
      variant="secondary"
    >
      <ArrowRight aria-label={submitLabel} size={20} strokeWidth={1.5} />
    </Button>
  );
}

function SearchResults({
  query,
  searchResults,
  stale,
  emptySearchTitle = `No results were found for '${query}'`,
  emptySearchSubtitle = 'Please try another search.',
  errors,
}: {
  query: string;
  searchParamName: string;
  emptySearchTitle?: string;
  emptySearchSubtitle?: string;
  searchResults: SearchResult[] | null;
  stale: boolean;
  errors?: string[];
}) {
  if (query === '') return null;

  if (errors != null && errors.length > 0) {
    if (stale) return null;

    return (
      <div className="flex flex-col border-t border-[var(--nav-search-divider,hsl(var(--contrast-100)))] p-6">
        {errors.map((error) => (
          <FormStatus key={error} type="error">
            {error}
          </FormStatus>
        ))}
      </div>
    );
  }

  if (searchResults == null || searchResults.length === 0) {
    if (stale) return null;

    return (
      <div className="flex flex-col border-t border-[var(--nav-search-divider,hsl(var(--contrast-100)))] p-6">
        <p className="text-2xl font-medium text-[var(--nav-search-empty-title,hsl(var(--foreground)))]">
          {emptySearchTitle}
        </p>
        <p className="text-[var(--nav-search-empty-subtitle,hsl(var(--contrast-500)))]">
          {emptySearchSubtitle}
        </p>
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'flex flex-1 flex-col overflow-y-auto border-t border-[var(--nav-search-divider,hsl(var(--contrast-100)))] @2xl:flex-row',
        stale && 'opacity-50',
      )}
    >
      {searchResults.map((result, index) => {
        switch (result.type) {
          case 'links': {
            return (
              <section
                aria-label={result.title}
                className="flex w-full flex-col gap-1 border-b border-[var(--nav-search-divider,hsl(var(--contrast-100)))] p-5 @2xl:max-w-80 @2xl:border-b-0 @2xl:border-r"
                key={`result-${index}`}
              >
                <h3 className="mb-4 font-[family-name:var(--nav-search-result-title-font-family,var(--font-family-mono))] text-sm uppercase text-[var(--nav-search-result-title,hsl(var(--foreground)))]">
                  {result.title}
                </h3>
                <ul role="listbox">
                  {result.links.map((link, i) => (
                    <li key={i}>
                      <Link
                        className="block bg-[var(--nav-search-result-link-background,transparent)] px-3 py-4 font-[family-name:var(--nav-search-result-link-font-family,var(--font-family-body))] font-semibold text-[var(--nav-search-result-link-text,hsl(var(--contrast-500)))] ring-[var(--nav-focus,hsl(var(--primary)))] transition-colors hover:bg-[var(--nav-search-result-link-background-hover,hsl(var(--contrast-100)))] hover:text-[var(--nav-search-result-link-text-hover,hsl(var(--foreground)))] focus-visible:outline-0 focus-visible:ring-2"
                        href={link.href}
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            );
          }

          case 'products': {
            return (
              <section
                aria-label={result.title}
                className="flex w-full flex-col gap-5 p-5"
                key={`result-${index}`}
              >
                <h3 className="font-[family-name:var(--nav-search-result-title-font-family,var(--font-family-mono))] text-sm uppercase text-[var(--nav-search-result-title,hsl(var(--foreground)))]">
                  {result.title}
                </h3>
                <ul
                  className="grid w-full grid-cols-2 gap-5 @xl:grid-cols-4 @2xl:grid-cols-2 @4xl:grid-cols-4"
                  role="listbox"
                >
                  {result.products.map((product) => (
                    <li key={product.id}>
                      <ProductCard
                        imageSizes="(min-width: 42rem) 25vw, 50vw"
                        product={{
                          id: product.id,
                          title: product.title,
                          href: product.href,
                          price: product.price,
                          image: product.image,
                        }}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            );
          }

          default:
            return null;
        }
      })}
    </div>
  );
}

const useSwitchLocale = () => {
  const pathname = usePathname();
  const router = useRouter();
  const params = useParams();
  const searchParams = useSearchParams();

  return useCallback(
    (locale: string) =>
      router.push(
        // @ts-expect-error -- TypeScript will validate that only known `params`
        // are used in combination with a given `pathname`. Since the two will
        // always match for the current route, we can skip runtime checks.
        { pathname, params, query: Object.fromEntries(searchParams.entries()) },
        { locale },
      ),
    [pathname, params, router, searchParams],
  );
};

function LocaleSwitcher({
  locales,
  activeLocaleId,
  className,
}: {
  activeLocaleId?: string;
  locales: [Locale, ...Locale[]];
  className?: string;
}) {
  const activeLocale = locales.find((locale) => locale.id === activeLocaleId);
  const [isPending, startTransition] = useTransition();
  const switchLocale = useSwitchLocale();

  return (
    <div className={className}>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger
          className={clsx(
            'flex items-center gap-1 text-xs uppercase transition-opacity disabled:opacity-30',
            navButtonClassName,
          )}
          disabled={isPending}
        >
          {activeLocale?.id ?? locales[0].id}
          <ChevronDown size={16} strokeWidth={1.5} />
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            className="z-50 max-h-80 overflow-y-scroll rounded-xl bg-[var(--nav-locale-background,hsl(var(--background)))] p-2 shadow-xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 @4xl:w-32 @4xl:@4xl:p-2"
            sideOffset={16}
          >
            {locales.map(({ id, label }) => (
              <DropdownMenu.Item
                className={clsx(
                  'cursor-default bg-[var(--nav-locale-link-background,transparent)] px-2.5 py-2 font-[family-name:var(--nav-locale-link-font-family,var(--font-family-body))] text-sm font-medium text-[var(--nav-locale-link-text,hsl(var(--contrast-400)))] outline-none ring-[var(--nav-focus,hsl(var(--primary)))] transition-colors hover:bg-[var(--nav-locale-link-background-hover,hsl(var(--contrast-100)))] hover:text-[var(--nav-locale-link-text-hover,hsl(var(--foreground)))]',
                  {
                    'text-[var(--nav-locale-link-text-selected,hsl(var(--foreground)))]':
                      id === activeLocaleId,
                  },
                )}
                key={id}
                onSelect={() => startTransition(() => switchLocale(id))}
              >
                {label}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}

function CurrencyForm({
  action,
  currencies,
  activeCurrencyId,
  switchCurrencyLabel = 'Switch currency',
  className,
}: {
  activeCurrencyId?: string;
  action: CurrencyAction;
  currencies: [Currency, ...Currency[]];
  switchCurrencyLabel?: string;
  className?: string;
}) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [lastResult, formAction] = useActionState(action, null);
  const activeCurrency = currencies.find((currency) => currency.id === activeCurrencyId);

  useEffect(() => {
    // eslint-disable-next-line no-console
    if (lastResult?.error) console.log(lastResult.error);
  }, [lastResult?.error]);

  return (
    <div className={className}>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger
          className={clsx(
            'flex items-center gap-1 text-xs uppercase transition-opacity disabled:opacity-30',
            navButtonClassName,
          )}
          disabled={isPending}
        >
          {activeCurrency?.label ?? currencies[0].label}
          <ChevronDown size={16} strokeWidth={1.5}>
            <title>{switchCurrencyLabel}</title>
          </ChevronDown>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            className="z-50 max-h-80 overflow-y-scroll rounded-xl bg-[var(--nav-locale-background,hsl(var(--background)))] p-2 shadow-xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 @4xl:w-32 @4xl:@4xl:p-2"
            sideOffset={16}
          >
            {currencies.map((currency) => (
              <DropdownMenu.Item
                className={clsx(
                  'cursor-default bg-[var(--nav-locale-link-background,transparent)] px-2.5 py-2 font-[family-name:var(--nav-locale-link-font-family,var(--font-family-body))] text-sm font-medium text-[var(--nav-locale-link-text,hsl(var(--contrast-400)))] outline-none ring-[var(--nav-focus,hsl(var(--primary)))] transition-colors hover:bg-[var(--nav-locale-link-background-hover,hsl(var(--contrast-100)))] hover:text-[var(--nav-locale-link-text-hover,hsl(var(--foreground)))]',
                  {
                    'text-[var(--nav-locale-link-text-selected,hsl(var(--foreground)))]':
                      currency.id === activeCurrencyId,
                  },
                )}
                key={currency.id}
                onSelect={() => {
                  // eslint-disable-next-line @typescript-eslint/require-await
                  startTransition(async () => {
                    const formData = new FormData();

                    formData.append('id', currency.id);
                    formAction(formData);

                    // This is needed to refresh the Data Cache after the product has been added to the cart.
                    // The cart id is not picked up after the first time the cart is created/updated.
                    router.refresh();
                  });
                }}
              >
                {currency.label}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}
